import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { useShallow } from "zustand/react/shallow";

// アップロード実行時に markFailed で store へ記録される失敗種別のみを定義する。
// file_type / file_size は submit 前の入力検証エラーで store には入らないため、
// 文言とあわせて表示層(medimo-lens/lib/upload-messages)側で扱う。
export type UploadErrorType =
  | "timeout"
  | "network"
  | "server"
  | "interrupted"
  | "file_read";

// uploading: S3 へのファイル転送中 / analyzing: 転送後のサーバー側 OCR 処理中 /
// retrying: 失敗したアップロードの再試行開始から新しいアップロードへの切り替え中
export type UploadStatus =
  | "uploading"
  | "analyzing"
  | "retrying"
  | "succeeded"
  | "failed";

// 進捗バーの配分。S3 転送完了で ANALYZING_PROGRESS(60%)、OCR 完了(markSucceeded)で
// 100% に到達させる。OCR は所要時間が読めないため、解析中はこの基準値で保持する。
export const ANALYZING_PROGRESS = 60;

export interface UploadItem {
  uploadId: string;
  sequence: number;
  fileId?: string;
  fileName: string;
  fileSize: number;
  contentType: string;
  status: UploadStatus;
  progress: number;
  displayProgress: number;
  errorType?: UploadErrorType;
  // 元 File は再試行とローカルプレビューに必要な間だけ保持し、サーバーの
  // サムネイル取得後は releaseFile、失敗を破棄した場合は removeUpload で解放する。
  file?: File;
}

type EnqueueInput = Omit<
  UploadItem,
  "uploadId" | "sequence" | "status" | "progress" | "displayProgress"
>;

interface MedimoLensUploadState {
  uploadsByConsult: Record<string, Record<string, UploadItem>>;
  nextSequence: number;
  enqueue: (consultId: string, item: EnqueueInput) => string;
  setFileId: (consultId: string, uploadId: string, fileId: string) => void;
  setProgress: (consultId: string, uploadId: string, progress: number) => void;
  setDisplayProgress: (
    consultId: string,
    uploadId: string,
    displayProgress: number,
  ) => void;
  markAnalyzing: (consultId: string, uploadId: string) => void;
  markSucceeded: (consultId: string, uploadId: string) => void;
  markFailed: (
    consultId: string,
    uploadId: string,
    errorType: UploadErrorType,
  ) => void;
  startRetry: (consultId: string, uploadId: string) => boolean;
  setFile: (consultId: string, uploadId: string, file: File) => void;
  releaseFile: (consultId: string, uploadId: string) => void;
  removeUpload: (consultId: string, uploadId: string) => void;
}

const INTERRUPTED_UPLOADS_STORAGE_KEY = "medimo-lens-interrupted-uploads-v1";
// ファイル名は PHI を含み得るため永続化せず、中断時は固定文言で表示する。
const INTERRUPTED_FILE_NAME = "処理未完了のファイル";

// transfer: S3 転送中(uploading/retrying)に中断＝失敗として復元する。
// analyzing: S3 転送完了後の OCR 解析中に中断(fileId 必須)。
type InterruptedUploadPhase = "transfer" | "analyzing";

type InterruptedUpload = {
  consultId: string;
  uploadId: string;
  sequence: number;
  savedAt: number;
  fileId?: string;
  fileSize: number;
  contentType: string;
  phase?: InterruptedUploadPhase;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isInterruptedUploadPhase = (
  value: unknown,
): value is InterruptedUploadPhase =>
  value === "transfer" || value === "analyzing";

const isInterruptedUpload = (value: unknown): value is InterruptedUpload => {
  if (!isRecord(value)) return false;
  return (
    typeof value.consultId === "string" &&
    typeof value.uploadId === "string" &&
    typeof value.sequence === "number" &&
    typeof value.savedAt === "number" &&
    typeof value.fileSize === "number" &&
    typeof value.contentType === "string" &&
    (value.fileId === undefined || typeof value.fileId === "string") &&
    (value.phase === undefined || isInterruptedUploadPhase(value.phase))
  );
};

const isInFlightUpload = (item: UploadItem | undefined): item is UploadItem =>
  item?.status === "uploading" ||
  item?.status === "retrying" ||
  item?.status === "analyzing";

const isInterruptedFailure = (
  item: UploadItem | undefined,
): item is UploadItem =>
  item?.status === "failed" && item.errorType === "interrupted";

const isPersistedUpload = (item: UploadItem | undefined): item is UploadItem =>
  isInFlightUpload(item) || isInterruptedFailure(item);

const getPersistedUploadItemKey = (
  item: UploadItem | undefined,
): string | undefined => {
  if (!isPersistedUpload(item)) return undefined;
  return [
    item.status,
    item.fileId ?? "",
    item.sequence,
    item.fileSize,
    item.contentType,
  ].join("\u0000");
};

export const hasInFlightUploadPersistenceChanges = (
  state: Pick<MedimoLensUploadState, "uploadsByConsult">,
  previousState: Pick<MedimoLensUploadState, "uploadsByConsult">,
): boolean => {
  const consultIds = new Set([
    ...Object.keys(state.uploadsByConsult),
    ...Object.keys(previousState.uploadsByConsult),
  ]);

  for (const consultId of consultIds) {
    const uploads = state.uploadsByConsult[consultId] ?? {};
    const previousUploads = previousState.uploadsByConsult[consultId] ?? {};
    const uploadIds = new Set([
      ...Object.keys(uploads),
      ...Object.keys(previousUploads),
    ]);

    for (const uploadId of uploadIds) {
      const item = uploads[uploadId];
      const previousItem = previousUploads[uploadId];
      if (
        getPersistedUploadItemKey(item) !==
        getPersistedUploadItemKey(previousItem)
      ) {
        return true;
      }
    }
  }

  return false;
};

export const saveInFlightUploads = (
  storage: Storage,
  state: Pick<MedimoLensUploadState, "uploadsByConsult">,
): void => {
  const savedAt = Date.now();
  const interruptedUploads = Object.entries(state.uploadsByConsult).flatMap(
    ([consultId, uploads]) =>
      Object.values(uploads)
        .filter(isPersistedUpload)
        .map(
          (item): InterruptedUpload => ({
            consultId,
            uploadId: item.uploadId,
            sequence: item.sequence,
            savedAt,
            fileId: item.fileId,
            fileSize: item.fileSize,
            contentType: item.contentType,
            phase:
              item.status === "analyzing" ||
              (item.errorType === "interrupted" && item.fileId)
                ? "analyzing"
                : "transfer",
          }),
        ),
  );

  try {
    if (interruptedUploads.length === 0) {
      storage.removeItem(INTERRUPTED_UPLOADS_STORAGE_KEY);
      return;
    }
    storage.setItem(
      INTERRUPTED_UPLOADS_STORAGE_KEY,
      JSON.stringify(interruptedUploads),
    );
  } catch {
    return;
  }
};

const restoredUploadItem = (item: InterruptedUpload): UploadItem => {
  // OCR 解析中の中断は元 File を復元できず再試行できないため、破棄可能な失敗として
  // 復元する。fileId はサーバーの未完了レコードを破棄時に削除するため保持する。
  if (item.phase === "analyzing" && item.fileId) {
    return {
      uploadId: item.uploadId,
      sequence: item.sequence,
      fileId: item.fileId,
      fileName: INTERRUPTED_FILE_NAME,
      fileSize: item.fileSize,
      contentType: item.contentType,
      status: "failed",
      progress: 0,
      displayProgress: 0,
      errorType: "interrupted",
      file: undefined,
    };
  }

  return {
    uploadId: item.uploadId,
    sequence: item.sequence,
    fileId: item.fileId,
    fileName: INTERRUPTED_FILE_NAME,
    fileSize: item.fileSize,
    contentType: item.contentType,
    status: "failed",
    progress: 0,
    displayProgress: 0,
    errorType: "interrupted",
    // 元 File は永続化しないため復元不可。再試行(要 File)は無効のまま破棄のみ許可する。
    file: undefined,
  };
};

export const restoreInterruptedUploads = (storage: Storage): void => {
  try {
    const serialized = storage.getItem(INTERRUPTED_UPLOADS_STORAGE_KEY);
    storage.removeItem(INTERRUPTED_UPLOADS_STORAGE_KEY);
    if (!serialized) return;
    const parsed: unknown = JSON.parse(serialized);
    if (!Array.isArray(parsed)) return;
    const interruptedUploads = parsed.filter(isInterruptedUpload);
    if (interruptedUploads.length === 0) return;

    useMedimoLensUploadStore.setState((state) => {
      const uploadsByConsult = { ...state.uploadsByConsult };
      for (const item of interruptedUploads) {
        uploadsByConsult[item.consultId] = {
          ...uploadsByConsult[item.consultId],
          [item.uploadId]: restoredUploadItem(item),
        };
      }
      const restoredNextSequence =
        Math.max(...interruptedUploads.map((item) => item.sequence)) + 1;
      return {
        uploadsByConsult,
        nextSequence: Math.max(state.nextSequence, restoredNextSequence),
      };
    });
  } catch {
    return;
  }
};

export const useMedimoLensUploadStore = create<MedimoLensUploadState>()(
  devtools(
    immer((set, get) => ({
      uploadsByConsult: {},
      nextSequence: 0,

      enqueue(consultId, item) {
        const uploadId = crypto.randomUUID();
        const sequence = get().nextSequence;
        set((state) => {
          state.nextSequence = sequence + 1;
          state.uploadsByConsult[consultId] ??= {};
          state.uploadsByConsult[consultId][uploadId] = {
            ...item,
            uploadId,
            sequence,
            status: "uploading",
            progress: 0,
            displayProgress: 0,
          };
        });
        return uploadId;
      },

      setFileId(consultId, uploadId, fileId) {
        set((state) => {
          const item = state.uploadsByConsult[consultId]?.[uploadId];
          if (item) item.fileId = fileId;
        });
      },

      setProgress(consultId, uploadId, progress) {
        set((state) => {
          const item = state.uploadsByConsult[consultId]?.[uploadId];
          if (item) item.progress = progress;
        });
      },

      setDisplayProgress(consultId, uploadId, displayProgress) {
        set((state) => {
          const item = state.uploadsByConsult[consultId]?.[uploadId];
          if (item && displayProgress > item.displayProgress) {
            item.displayProgress = displayProgress;
          }
        });
      },

      // S3 転送完了後、サーバー側 OCR 処理に入ったことを表す。実進捗は OCR 完了
      // (markSucceeded)まで 100% にせず、解析フェーズの基準値(60%)で保持する。
      markAnalyzing(consultId, uploadId) {
        set((state) => {
          const item = state.uploadsByConsult[consultId]?.[uploadId];
          if (item) {
            item.status = "analyzing";
            item.progress = ANALYZING_PROGRESS;
          }
        });
      },

      markSucceeded(consultId, uploadId) {
        set((state) => {
          const item = state.uploadsByConsult[consultId]?.[uploadId];
          if (item) {
            item.status = "succeeded";
            item.progress = 100;
          }
        });
      },

      markFailed(consultId, uploadId, errorType) {
        set((state) => {
          const item = state.uploadsByConsult[consultId]?.[uploadId];
          if (item) {
            // 元 File を持たない解析中アイテムは再試行できないため未完了表記へ戻す。
            if (item.status === "analyzing" && !item.file) {
              item.fileName = INTERRUPTED_FILE_NAME;
            }
            item.status = "failed";
            item.errorType = errorType;
          }
        });
      },

      startRetry(consultId, uploadId) {
        // 判定から状態更新まで同期的に完了させ、別画面や再マウント後からの
        // 同一アップロードに対する再試行開始を一度だけ許可する。
        const item = get().uploadsByConsult[consultId]?.[uploadId];
        if (item?.status !== "failed") return false;

        set((state) => {
          const currentItem = state.uploadsByConsult[consultId]?.[uploadId];
          if (currentItem?.status === "failed") {
            currentItem.status = "retrying";
          }
        });
        return true;
      },

      // 選択された File をメモリ上へ読み切ったものへ差し替える。picker 由来の File は
      // 送信時の再読み取りに失敗し得るため、再試行とローカルプレビューも
      // 読み取り済みのバイト列に対して行う(MEDIMO-3960)。
      setFile(consultId, uploadId, file) {
        set((state) => {
          const item = state.uploadsByConsult[consultId]?.[uploadId];
          if (item) item.file = file;
        });
      },

      releaseFile(consultId, uploadId) {
        set((state) => {
          const item = state.uploadsByConsult[consultId]?.[uploadId];
          if (item) item.file = undefined;
        });
      },

      // 再試行時の再アップロード前と、OCR結果付きの確定表示へ切り替える際に呼ばれる。
      // consult_done に到達しない成功エントリは、画面表示のためセッション中保持される。
      removeUpload(consultId, uploadId) {
        set((state) => {
          delete state.uploadsByConsult[consultId]?.[uploadId];
        });
      },
    })),
  ),
);

const emptyUploads: UploadItem[] = [];

export const useConsultUploads = (
  consultId: string | undefined,
): UploadItem[] => {
  return useMedimoLensUploadStore(
    useShallow((state) => {
      if (!consultId) return emptyUploads;
      const uploads = state.uploadsByConsult[consultId];
      if (!uploads) return emptyUploads;
      return Object.values(uploads).sort((a, b) => b.sequence - a.sequence);
    }),
  );
};

// S3 転送完了後の analyzing はブラウザを離れてもサーバー側で継続するため、
// 離脱によって中断し得る転送中・再試行開始中だけを対象にする。
export const useHasInFlightUploads = (): boolean => {
  return useMedimoLensUploadStore((state) =>
    Object.values(state.uploadsByConsult).some((uploads) =>
      Object.values(uploads).some(
        (item) => item.status === "uploading" || item.status === "retrying",
      ),
    ),
  );
};
